import { create } from "zustand";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";
import toast from "react-hot-toast";

const usePaymentStore = create((set, get) => ({

    isCreatingOrder: false,
    isVerifyingPayment: false,

    /* ======================================================
       CREATE CASHFREE ORDER  (LIVE READY)
       Backend: POST /subscriptions/builder/create-order
    ====================================================== */
    createCashfreeOrder: async (amount, durationMonths = 12) => {
        set({ isCreatingOrder: true });

        try {
            const { token, user } = useAuthStore.getState();

            if (!user?.builderId) {
                toast.error("Builder profile not found");
                return null;
            }

            const response = await axiosInstancev1.post(
                "/subscriptions/builder/create-order",
                {
                    builderProfileId: user.builderId,
                    amount,
                    durationMonths,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (!response.data.success || !response.data.data) {
                toast.error(response.data.message || "Unable to create order");
                return null;
            }

            const order = response.data.data;

            return {
                merchantOrderId: order.merchantOrderId,
                cashfreeOrderId: order.cashfreeOrderId,
                paymentSessionId: order.paymentSessionId,
                paymentLink: order.paymentLink || null,
                signature: order.signature || null,
            };

        } catch (error) {
            console.error("Cashfree Order Error:", error);
            toast.error(error.response?.data?.message || "Order creation failed");
            return null;

        } finally {
            set({ isCreatingOrder: false });
        }
    },

    /* ======================================================
       VERIFY PAYMENT (LIVE READY)
       Backend: POST /subscriptions/builder/verify
    ====================================================== */
    verifyCashfreePayment: async (merchantOrderId) => {
        set({ isVerifyingPayment: true });

        try {
            const { token } = useAuthStore.getState();

            const response = await axiosInstancev1.post(
                "/subscriptions/builder/verify",
                { merchantOrderId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (response.data.success) {
                // Success message will be shown after redirect
                return { success: true, subscription: response.data.data };
            }

            toast.error(response.data.message || "Verification failed");
            return { success: false };

        } catch (error) {
            console.error("Verify Payment Error:", error);
            toast.error(error.response?.data?.message || "Payment verification failed");
            return { success: false };

        } finally {
            set({ isVerifyingPayment: false });
        }
    },

    /* ======================================================
       PROJECT VALUE ENTRIES (Builder manages project budget)
    ====================================================== */
    projectValueEntries: [],
    isValueEntriesLoading: false,
    isValueEntrySubmitting: false,

    fetchProjectValueEntries: async (projectId) => {
        if (!projectId) {
            set({ projectValueEntries: [], isValueEntriesLoading: false });
            return;
        }
        set({ isValueEntriesLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required to fetch project value history.");
            set({ isValueEntriesLoading: false });
            return;
        }
        try {
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/value-entries`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                const sortedEntries = (response.data.data || []).sort((a, b) => {
                    const dateComparison = new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime();
                    if (dateComparison !== 0) return dateComparison;
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                });
                set({ projectValueEntries: sortedEntries, isValueEntriesLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch project value history.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch project value history.";
            toast.error(errorMsg);
            set({ projectValueEntries: [], isValueEntriesLoading: false });
        }
    },

    addProjectValueEntry: async (projectId, entryData) => {
        set({ isValueEntrySubmitting: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isValueEntrySubmitting: false });
            return false;
        }
        try {
            const formatDateForAPI = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return null;
                if (dateString.length === 10 && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return new Date(dateString + "T00:00:00.000Z").toISOString();
                }
                return date.toISOString();
            };

            const payload = {
                date: formatDateForAPI(entryData.entry_date),
                comment: entryData.comment,
                status: "Pending",
                amount: parseFloat(entryData.amount)
            };

            const response = await axiosInstancev1.post(
                `/projects/${projectId}/value-entries`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success(response.data.message || "Project value entry added successfully.");
                await get().fetchProjectValueEntries(projectId);
                return true;
            } else {
                throw new Error(response.data.message || "Failed to add project value entry.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.errors ?
                Object.values(error.response.data.errors).join(', ') :
                (error.response?.data?.message || "Could not add project value entry.");
            toast.error(errorMsg);
            return false;
        } finally {
            set({ isValueEntrySubmitting: false });
        }
    },

    /* ======================================================
       BUILDER PAYMENT REQUESTS
    ====================================================== */
    builderPaymentRequests: [],
    isPaymentRequestsLoading: false,
    isPaymentRequestSubmitting: false,
    isRequestSubmitting: false,

    fetchBuilderPaymentRequests: async (projectId) => {
        if (!projectId) {
            set({ builderPaymentRequests: [], isPaymentRequestsLoading: false });
            return;
        }
        set({ isPaymentRequestsLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isPaymentRequestsLoading: false });
            return;
        }
        try {
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/builder-payment-requests`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                const sortedRequests = (response.data.data || []).sort((a, b) => {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                });
                set({ builderPaymentRequests: sortedRequests, isPaymentRequestsLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch payment requests.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch payment requests.";
            toast.error(errorMsg);
            set({ builderPaymentRequests: [], isPaymentRequestsLoading: false });
        }
    },

    createBuilderPaymentRequest: async (projectId, requestData) => {
        set({ isPaymentRequestSubmitting: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isPaymentRequestSubmitting: false });
            return false;
        }
        try {
            const payload = {
                ...requestData,
                amount: parseFloat(requestData.amount)
            };

            const response = await axiosInstancev1.post(
                `/projects/${projectId}/builder-payment-requests`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success(response.data.message || "Payment request created successfully.");
                set((state) => ({
                    builderPaymentRequests: [response.data.data, ...state.builderPaymentRequests],
                }));
                return true;
            } else {
                throw new Error(response.data.message || "Failed to create payment request.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.errors ?
                Object.values(error.response.data.errors).join(', ') :
                (error.response?.data?.message || "Could not create payment request.");
            toast.error(errorMsg);
            return false;
        } finally {
            set({ isPaymentRequestSubmitting: false });
        }
    },

    isConfirmingPayment: false,

    confirmPaymentReceived: async (projectId, requestId, paymentDetails) => {
        set({ isConfirmingPayment: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isConfirmingPayment: false });
            return { success: false };
        }
        try {
            const payload = {
                payment_status: "Paid",
                payment_method: paymentDetails.paymentMethod,
                transaction_reference_id: paymentDetails.transactionReferenceId || null,
                payment_confirmation_comment: paymentDetails.comment || null,
            };

            const response = await axiosInstancev1.patch(
                `/projects/${projectId}/builder-payment-requests/${requestId}/confirm-payment`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success && response.data.data) {
                toast.success(response.data.message || "Payment confirmed successfully.");
                set((state) => ({
                    builderPaymentRequests: state.builderPaymentRequests.map((req) =>
                        req.id === requestId ? { ...req, ...response.data.data } : req
                    ),
                }));
                get().fetchTransactionHistory(projectId);
                return { success: true, data: response.data.data, transaction_id: response.data.transaction_id };
            } else {
                throw new Error(response.data.message || "Failed to confirm payment.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.errors
                ? Object.values(error.response.data.errors).join(', ')
                : error.response?.data?.message || "Could not confirm payment.";
            toast.error(errorMsg);
            return { success: false, message: errorMsg };
        } finally {
            set({ isConfirmingPayment: false });
        }
    },

    /* ======================================================
       TRANSACTION HISTORY
    ====================================================== */
    transactionHistory: [],
    isTransactionHistoryLoading: false,

    projectTransactions: [],
    isProjectTransactionsLoading: false,

    fetchTransactionHistory: async (projectId) => {
        if (!projectId) {
            set({ projectTransactions: [], isProjectTransactionsLoading: false });
            return;
        }
        set({ isProjectTransactionsLoading: true });
        const { token } = useAuthStore.getState();
        if (!token) {
            toast.error("Authentication required.");
            set({ isProjectTransactionsLoading: false });
            return;
        }
        try {
            const response = await axiosInstancev1.get(
                `/projects/${projectId}/transactions`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                const sortedTransactions = (response.data.data || []).sort((a, b) => {
                    return new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime();
                });
                set({ projectTransactions: sortedTransactions, isProjectTransactionsLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch project payment history.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Could not fetch project payment history.";
            toast.error(errorMsg);
            set({ projectTransactions: [], isProjectTransactionsLoading: false });
        }
    },

    fetchProjectTransactions: async (projectId) => {
        return get().fetchTransactionHistory(projectId);
    },

    /* ======================================================
       REFRESH ALL DATA
    ====================================================== */
    refreshAllPaymentData: async (projectId) => {
        if (!projectId) return;

        await Promise.all([
            get().fetchProjectValueEntries(projectId),
            get().fetchBuilderPaymentRequests(projectId),
            get().fetchTransactionHistory(projectId)
        ]);
    },

    // Razorpay payment functions (for subscription payments)
    isCreatingOrder: false,
    isVerifyingPayment: false,

    createCashfreeOrder: async (amount, durationMonths = 12) => {
        set({ isCreatingOrder: true });

        try {
            const { token, user } = useAuthStore.getState();

            if (!user?.builderId) {
                toast.error("Builder profile not found");
                return null;
            }

            const response = await axiosInstancev1.post(
                "/subscriptions/builder/create-order",
                {
                    builderProfileId: user.builderId,
                    amount,
                    durationMonths,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (!response.data.success || !response.data.data) {
                toast.error(response.data.message || "Unable to create order");
                return null;
            }

            const order = response.data.data;

            return {
                merchantOrderId: order.merchantOrderId,
                cashfreeOrderId: order.cashfreeOrderId,
                paymentSessionId: order.paymentSessionId,
                paymentLink: order.paymentLink || null,
                signature: order.signature || null,
            };

        } catch (error) {
            console.error("Cashfree Order Error:", error);
            toast.error(error.response?.data?.message || "Order creation failed");
            return null;

        } finally {
            set({ isCreatingOrder: false });
        }
    },

    verifyCashfreePayment: async (merchantOrderId) => {
        set({ isVerifyingPayment: true });

        try {
            const { token } = useAuthStore.getState();

            const response = await axiosInstancev1.post(
                "/subscriptions/builder/verify",
                { merchantOrderId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (response.data.success) {
                return { success: true, subscription: response.data.data };
            }

            toast.error(response.data.message || "Verification failed");
            return { success: false };

        } catch (error) {
            console.error("Verify Payment Error:", error);
            toast.error(error.response?.data?.message || "Payment verification failed");
            return { success: false };

        } finally {
            set({ isVerifyingPayment: false });
        }
    },

}));

export default usePaymentStore;